/**
 * The one occupancy figure (#2175 B4, D3). Port of
 * mobile-management/src/features/cockpit/occupancy.ts; occupancy.test.ts
 * carries the phone's fixtures.
 *
 * Occupancy is the WEIGHTED headcount divided by the weighted places, summed
 * over the groups in scope (babies count more than one place). Summing before
 * dividing gives every group the weight of its size, so a small group at 100%
 * cannot pull the total up as the plain mean of percentages does. The raw
 * children and places are kept next to it so the denominator stays visible.
 * Groups whose maximum is not configured carry no percentage and are left out
 * of the ratio, but their children still count.
 */

/** The fields of a `capacity/overview` group these figures read. */
export interface OccupancyGroup {
  group_id: string;
  group_name?: string | null;
  childcare_id?: string | null;
  headcount: number;
  max_headcount?: number | null;
  weighted_headcount: number;
  max_weighted_headcount?: number | null;
  weighted_utilization_percent?: number | null;
  headcount_utilization_percent?: number | null;
}

export interface OccupancyFigures {
  /** Sum of the weighted headcounts of the groups with a weighted maximum. */
  weighted: number;
  /** Sum of the weighted maxima; 0 when no group in scope has one. */
  maxWeighted: number;
  /** Children booked at the day's peak, every group. */
  children: number;
  /** Licensed places (`max_headcount`) of the groups that have one. */
  places: number;
  /** Rounded weighted occupancy in percent, or null without a configured maximum. */
  percent: number | null;
}

/** A `capacity/overview` group as the generated client types it (every field optional). */
export interface CapacityOverviewGroupLike {
  group_id?: string;
  group_name?: string;
  childcare_id?: string;
  headcount?: number;
  max_headcount?: number;
  weighted_headcount?: number;
  max_weighted_headcount?: number;
  weighted_utilization_percent?: number;
  headcount_utilization_percent?: number;
}

/** The overview group as these figures read it (web only, #2175 F1). */
export function toOccupancyGroup(g: CapacityOverviewGroupLike): OccupancyGroup {
  return {
    group_id: g.group_id ?? "",
    group_name: g.group_name,
    childcare_id: g.childcare_id,
    headcount: g.headcount ?? 0,
    max_headcount: g.max_headcount,
    weighted_headcount: g.weighted_headcount ?? g.headcount ?? 0,
    max_weighted_headcount: g.max_weighted_headcount,
    weighted_utilization_percent: g.weighted_utilization_percent,
    headcount_utilization_percent: g.headcount_utilization_percent,
  };
}

/**
 * Weighted occupancy with one decimal, for the explorer tiles that print
 * "70.0 %" (#2175 F1); null without a weighted maximum.
 */
export function weightedPercentExact(figures: OccupancyFigures): number | null {
  return figures.maxWeighted > 0 ? (figures.weighted / figures.maxWeighted) * 100 : null;
}

/**
 * Unweighted occupancy: children booked over licensed places, each counted once. Only
 * where the raw ratio is wanted next to the weighted one (Master data); null
 * without places.
 */
export function headcountPercent(figures: OccupancyFigures): number | null {
  return figures.places > 0 ? (figures.children / figures.places) * 100 : null;
}

export function occupancyFigures(groups: readonly OccupancyGroup[]): OccupancyFigures {
  let weighted = 0;
  let maxWeighted = 0;
  let children = 0;
  let places = 0;
  for (const g of groups) {
    children += g.headcount;
    if (g.max_headcount != null) places += g.max_headcount;
    if (g.max_weighted_headcount != null && g.max_weighted_headcount > 0) {
      weighted += g.weighted_headcount;
      maxWeighted += g.max_weighted_headcount;
    }
  }
  return {
    weighted,
    maxWeighted,
    children,
    places,
    percent: maxWeighted > 0 ? Math.round((weighted / maxWeighted) * 100) : null,
  };
}

/** The group's own weighted percentage, falling back to the raw one. */
export function groupPercent(group: OccupancyGroup): number | null {
  const pct = group.weighted_utilization_percent ?? group.headcount_utilization_percent;
  return pct == null ? null : Math.round(pct);
}

export type OccupancyAccent = "good" | "warn" | "bad" | "neutral";

/** Good below 90%, warn from 90%, bad at or over 100%; neutral when unknown. */
export function occupancyAccent(percent: number | null): OccupancyAccent {
  if (percent == null) return "neutral";
  if (percent >= 100) return "bad";
  if (percent >= 90) return "warn";
  return "good";
}

export interface OccupancySection<G extends OccupancyGroup = OccupancyGroup> {
  /** The childcare id, or `'other'` for groups without one. */
  key: string;
  /** The childcare name; undefined for groups without a childcare. */
  name?: string;
  groups: G[];
  figures: OccupancyFigures;
}

/**
 * Groups sectioned by their childcare, in the order of `childcares` (then any
 * childcare the list does not know, by id, then the groups without one). Each
 * section carries its own summed figures. The group order inside a section is
 * the server's.
 */
export function occupancySections<G extends OccupancyGroup>(
  groups: readonly G[],
  childcares: readonly { id: string; name: string }[] | undefined,
): OccupancySection<G>[] {
  const byChildcare = new Map<string, G[]>();
  const other: G[] = [];
  for (const g of groups) {
    if (g.childcare_id) {
      const list = byChildcare.get(g.childcare_id) ?? [];
      list.push(g);
      byChildcare.set(g.childcare_id, list);
    } else {
      other.push(g);
    }
  }

  const sections: OccupancySection<G>[] = [];
  const seen = new Set<string>();
  for (const c of childcares ?? []) {
    const list = byChildcare.get(c.id);
    if (!list) continue;
    seen.add(c.id);
    sections.push({ key: c.id, name: c.name, groups: list, figures: occupancyFigures(list) });
  }
  for (const [id, list] of byChildcare) {
    if (seen.has(id)) continue;
    sections.push({ key: id, groups: list, figures: occupancyFigures(list) });
  }
  if (other.length > 0) {
    sections.push({ key: "other", groups: other, figures: occupancyFigures(other) });
  }
  return sections;
}

/** One group's weekly buckets, as `/capacity/overview/range` sends them. */
export interface OccupancyRangeGroup {
  group_id: string;
  weeks: {
    summary: {
      average_weighted_utilization: number;
      average_headcount_utilization: number;
    };
  }[];
}

/**
 * The occupancy sparkline: one point per week of the range, the occupancy of
 * the groups in scope weighted like the headline figure: each group by its
 * weighted places (`weightedPlaces`, today's maxima, since the range response
 * carries none), so the last point matches sum(weighted) / sum(max weighted).
 * Without weighted places the groups count equally, and a group without
 * weighted places (weighted utilization 0 while children are booked) counts
 * with its head-count utilization so it does not pull the line to zero.
 */
export function occupancySparkline(
  groups: readonly OccupancyRangeGroup[] | undefined,
  inScope: (groupId: string) => boolean,
  weightedPlaces?: ReadonlyMap<string, number>,
): number[] {
  return occupancySparklinePoints(groups, inScope, weightedPlaces).map((p) => p.value);
}

/**
 * `occupancySparkline` with the index of the week each point stands for, so a
 * caller can label the point (the Dashboard reads the week's start date).
 */
export function occupancySparklinePoints(
  groups: readonly OccupancyRangeGroup[] | undefined,
  inScope: (groupId: string) => boolean,
  weightedPlaces?: ReadonlyMap<string, number>,
): { week: number; value: number }[] {
  const scoped = (groups ?? []).filter((g) => inScope(g.group_id));
  const weeks = Math.max(0, ...scoped.map((g) => g.weeks.length));
  const weightOf = (id: string): number => {
    if (!weightedPlaces || weightedPlaces.size === 0) return 1;
    return weightedPlaces.get(id) ?? 0;
  };
  const out: { week: number; value: number }[] = [];
  for (let i = 0; i < weeks; i += 1) {
    let sum = 0;
    let weight = 0;
    for (const g of scoped) {
      const s = g.weeks[i]?.summary;
      if (!s) continue;
      const w = weightOf(g.group_id);
      if (w <= 0) continue;
      const value =
        s.average_weighted_utilization > 0 ? s.average_weighted_utilization : s.average_headcount_utilization;
      sum += value * w;
      weight += w;
    }
    if (weight > 0) out.push({ week: i, value: sum / weight });
  }
  return out;
}
